import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { Group, Location, PagedResult } from '@core/interfaces';
import { chunk } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GroupService } from '../../../../services/groups-api.service';

@Component({
  selector: 'link-locations',
  templateUrl: './link-locations.component.html',
  styleUrls: ['../tab-locations.component.scss']
})
export class LinkLocationsComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef, static: true })
  public form: ElementRef<HTMLDivElement>;

  @ViewChild('paginator', { static: false })
  public paginator: FcPaginatorComponent;

  public page: PagedResult<Location> = null;
  public searchInput = new FormControl();
  public isLoading = true;
  public isSaving = false;
  public isAllSelected = false;
  public currentPage = 0;
  public pageSize = 5;
  public isPageChanging = false;
  public locationToAdd = new Map<string, boolean>();
  public linkAllLocations = false;
  private group: Group;

  private readonly reload = new Subject<void>();
  private readonly removeSubscriptions = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<any>,
    private groupService: GroupService,
  ) {
    this.group = data.group;
  }

  public ngOnInit(): void {

    this.searchInput.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.isLoading = true;
      if (this.paginator) {
        this.paginator.firstPage();
      } else {
        this.reload.next();
      }
    });

    this.reload.pipe(
      takeUntil(this.removeSubscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchLocations()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((page) => {
      this.page = page;
      this.form.nativeElement.scrollTo({ top: 0 });
    });

    this.reload.next();
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public toggleLocationLinkedToGroup(checked: boolean, location: Location): void {
    if (this.linkAllLocations) {
      this.linkAllLocations = false;

      if (this.locationToAdd.has(location.id)) {
        this.locationToAdd.delete(location.id);
      } else {
        this.locationToAdd.set(location.id, checked);
      }

      this.isAllSelected = this.checkIfIsAllSelected();
      return;
    }

    if (this.locationToAdd.has(location.id)) {
      this.locationToAdd.delete(location.id);
    } else {
      this.locationToAdd.set(location.id, checked);
    }

    this.isAllSelected = this.checkIfIsAllSelected();
  }

  private async searchLocations(): Promise<PagedResult<Location>> {
    return this.groupService.listLocations({
      groupId: this.group.id,
      limit: this.pageSize,
      offset: this.currentPage * this.pageSize,
      where: {
        formattedAddressOrDocumentNumber_contains: this.searchInput.value || '',
      }
    });
  }

  public toggleAllSelected(checked: boolean): void {
    const locations = (this.page && this.page.items) || [];
    for (const location of locations) {
      if (!location.groupsConnection.items.length) {
        this.toggleLocationLinkedToGroup(checked, location);
      }
    }

    this.isAllSelected = this.checkIfIsAllSelected();
  }

  private checkIfIsAllSelected(): boolean {
    const locations = (this.page && this.page.items) || [];
    return locations.length && locations.every((location) => location.groupsConnection.items.length || this.locationToAdd.has(location.id));
  }

  public async continue(): Promise<void> {
    this.isSaving = true;

    if (this.linkAllLocations) {
      this.isLoading = true;
      await this.groupService.linkAllLocationsToGroup({ groupId: this.group.id });
      this.isSaving = false;
      this.dialogRef.close({
        reload: true
      });
      return;
    }

    const keys = Array.from(this.locationToAdd.keys());
    for (const locationIds of chunk(keys, 100)) {
      await this.groupService.addLocationToGroup(locationIds.map((locationId) => ({
        locationId: locationId,
        groupId: this.group.id
      })));
    }

    this.isSaving = false;
    this.dialogRef.close({
      reload: true
    });
  }

  async onPageChange(event: PageEvent): Promise<void> {
    this.isPageChanging = true;
    this.currentPage = event.pageIndex;
    this.page = await this.searchLocations();
    this.isAllSelected = this.checkIfIsAllSelected();
    this.form.nativeElement.scrollTo({ top: 0 });
    this.isPageChanging = false;
    this.isLoading = false;
  }

  public async selectAllLocations(): Promise<void> {
    this.linkAllLocations = true;
  }
}
